/**
 * ID verification workspace (Brief 185) — the customer's verifications (read
 * view over GET /v1/idverify/verifications). Creating verifications is an
 * agent/API flow (POST /v1/idverify/verifications, which returns the hosted
 * handoff URL); this surface is the list + live-status view.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { BadgeCheck } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { fetchIdverifyVerifications, idverifyListKey } from '@/lib/queries';
import { ApiError } from '@/lib/fetcher';

function fmtDate(s?: string): string {
  if (!s) return '—';
  try {
    return format(parseISO(s), 'MMM d, yyyy');
  } catch {
    return s;
  }
}

export function IdverifyWorkspace() {
  const [page, setPage] = React.useState({ limit: 20, offset: 0 });
  const q = useQuery({
    queryKey: idverifyListKey(page),
    queryFn: () => fetchIdverifyVerifications(page),
  });
  const rows = q.data?.data ?? q.data?.verifications ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">ID verification</h1>
        <p className="text-muted-foreground">
          Your verifications and their live status. Start one via POST
          /v1/idverify/verifications — the response carries the hosted handoff
          URL.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Verifications</CardTitle>
          <CardDescription>Newest first.</CardDescription>
        </CardHeader>
        <CardContent>
          {q.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load verifications ({q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => q.refetch()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={BadgeCheck}
              title="No verifications yet"
              description="Start a verification via POST /v1/idverify/verifications — it appears here with live status."
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Verification</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell className="font-mono text-xs">{v.id}</TableCell>
                      <TableCell>{v.type ?? '—'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{v.status ?? 'unknown'}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {fmtDate(v.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {q.data?.total !== undefined ? (
                <Pagination
                  total={q.data.total}
                  limit={q.data.limit ?? page.limit}
                  offset={q.data.offset ?? page.offset}
                  onChange={setPage}
                />
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
